import { useCallback, useMemo } from 'react'
import { LanguageConfigSource, MeetingLanguage, RoomSessionLanguageConfig } from '../models/Room'
import { UserLanguage } from '../models/User'
import { useGetLanguagesQuery } from '../store/globalConfigs/globalConfigs'
import { useAccount } from './useAccount'

export type LanguageOption = { id: MeetingLanguage; label: string; labelShort: string }

export const useLanguageOptions = () => {
  const { account } = useAccount()
  const { data: languagesData } = useGetLanguagesQuery({ includeBeta: true })

  const supportedSet = useMemo(
    () => new Set(account?.effectiveConfig.multiLanguageSupport?.supportedLanguages),
    [account?.effectiveConfig.multiLanguageSupport?.supportedLanguages],
  )

  const allOptions: LanguageOption[] = useMemo(
    () =>
      (languagesData?.data ?? []).map((item) => ({
        id: item.id as MeetingLanguage,
        label: item.displayName,
        labelShort: item.displayShortName,
      })),
    [languagesData],
  )

  const languageOptions: LanguageOption[] = useMemo(
    () => allOptions.filter((o) => o.id !== MeetingLanguage.AUTO_DETECT && supportedSet.has(o.id)),
    [allOptions, supportedSet],
  )

  const languageOptionsWithAutoDetect: LanguageOption[] = useMemo(
    () => allOptions.filter((o) => o.id === MeetingLanguage.AUTO_DETECT || supportedSet.has(o.id)),
    [allOptions, supportedSet],
  )

  const getLabel = useCallback((language?: MeetingLanguage | null): string => allOptions.find((o) => o.id === language)?.label ?? '', [allOptions])

  const getShortLabel = useCallback((language?: MeetingLanguage | null): string => allOptions.find((o) => o.id === language)?.labelShort ?? '', [allOptions])

  const getContactLabel = useCallback(
    (language?: UserLanguage | null): string => allOptions.find((o) => o.id === (language as string))?.label ?? '',
    [allOptions],
  )

  const getContactShortLabel = useCallback(
    (language?: UserLanguage | null): string | null => allOptions.find((o) => o.id === (language as string))?.labelShort ?? null,
    [allOptions],
  )

  return { languageOptions, languageOptionsWithAutoDetect, getLabel, getShortLabel, getContactLabel, getContactShortLabel }
}

export const isLanguageCalculated = (languageConfig: RoomSessionLanguageConfig): boolean =>
  languageConfig.source !== LanguageConfigSource.MANUAL && languageConfig.source !== LanguageConfigSource.DEFAULT

export const getLanguageSource = (languageConfig: RoomSessionLanguageConfig): string | null => {
  switch (languageConfig.source) {
    case LanguageConfigSource.CALCULATED_MAJORITY:
      return ' (most common)'
    case LanguageConfigSource.CALCULATED_ACCOUNT_CONFIG:
    case LanguageConfigSource.NO_CLIENTS_ACCOUNT_CONFIG:
      return ' (fallback)'
    case LanguageConfigSource.CALCULATED_UNAMBIGUOUS:
      return ' (all the same)'
    default:
      return null
  }
}
